import { DecimalPipe, formatDate } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { LocaleService } from '@next/core-lib/i18n';
import { DataSeries } from '@next/core-lib/models';
import { SelectedCustomer, SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { skipWhile, take } from 'rxjs/operators';

import { BrandingLogoSrc } from 'src/app/branding/branding.model';

import { getTenantName } from 'src/app/branding/store/branding.selectors';

import { EGG_MARKETING_STANDARDS } from '../../../../assets/data/eggMarketingStandards';
import {
  EggMarketingStandard,
  Flock,
  FlockWeekData,
  WeekKPI,
  WeekRegistrationKPIItem,
} from '../../models/flock.model';
import { FlockWeekDataSet } from '../../models/flock.model';
import { FlockWeekregistrationKpiService } from '../../services/flock-weekregistration-kpi.service';
import { setSelectedFlockId } from '../../store/actions/flock.actions';
import { getFlocks, getSelectedFlockWeekData } from '../../store/selectors/flock.selectors';

@Component({
  selector: 'app-flock-report',
  templateUrl: './flock-report.component.html',
  styleUrls: ['./flock-report.component.scss'],
  standalone: false,
})
export class FlockReportComponent {
  flock: Flock;
  title: string;
  ageStartProductionPeriod: number;
  mode: string;
  KPIweeks: WeekKPI[] = [];
  averages: WeekKPI;
  customer: SelectedCustomer;
  location: SelectedLocation;
  eggMarketingStandards: EggMarketingStandard[] = EGG_MARKETING_STANDARDS;
  flockWeekData: FlockWeekData[];
  flockWeekDataSeries: DataSeries<number, number>;

  showAgrifirmLogo = true;
  customizedLogoSrc: string = BrandingLogoSrc.PoultryNextLogo;

  constructor(
    private readonly store: Store<RootState>,
    private readonly activatedRoute: ActivatedRoute,
    private flockWeekregistrationKpiService: FlockWeekregistrationKpiService,
    private router: Router,
    @Inject(LocaleService) private locale: LocaleService,
    private decimalPipe: DecimalPipe,
  ) {
    const id = parseInt(this.activatedRoute.snapshot.params['id'] as string, 10);
    this.mode = (this.activatedRoute.snapshot.params['mode'] as string) ?? 'all';
    this.title = this.locale.translate(`report.title_${this.mode}`);
    if (!!id && id > 0) {
      this.store.dispatch(setSelectedFlockId({ id, save: false, reloadWeekData: true }));

      combineLatest([
        this.store.select(getFlocks),
        this.store.select(UserSelectors.selectCustomer),
        this.store.select(UserSelectors.selectLocation),
        this.store.select(getSelectedFlockWeekData),
      ])
        .pipe(
          skipWhile(
            ([flocks, customer, location, flockWeekData]) =>
              !flocks?.some((flock) => flock.id === id) || !customer || !location || !flockWeekData,
          ),
          take(1),
        )
        .subscribe(
          ([flocks, customer, location, flockWeekData]: [
            Flock[],
            SelectedCustomer,
            SelectedLocation,
            FlockWeekDataSet,
          ]) => {
            this.customer = customer;
            this.location = location;
            this.flock = flocks.find((f) => f.id === id);
            if (!!this.flock && !!flockWeekData) {
              this.setReportData(flockWeekData);
            } else {
              this.router.navigate(['/flock']);
            }
          },
        );
    }

    this.store
      .select(getTenantName)
      .pipe(take(1))
      .subscribe((tenantName) => {
        if (tenantName === 'Agravis') {
          this.customizedLogoSrc = BrandingLogoSrc.AgravisLogo;
          this.showAgrifirmLogo = false;
        }
      });
  }

  private setReportData(flockWeekData: FlockWeekDataSet): void {
    this.flockWeekData = flockWeekData.data;
    this.flockWeekDataSeries = flockWeekData.dataSeries;

    if (this.flockWeekData.length === 0) {
      this.KPIweeks = [];
      this.averages = {};
      this.refreshPrintLayout();
      return;
    }

    this.KPIweeks = this.flockWeekData
      .slice()
      .sort((a, b) => (a.week > b.week ? 1 : -1))
      .map((weekData) =>
        this.flockWeekregistrationKpiService.getWeekKPI(weekData, this.flockWeekDataSeries),
      );
    this.averages = this.flockWeekregistrationKpiService.getAveragesWeekKPI(
      this.flockWeekData,
      this.flockWeekDataSeries,
    );
    this.refreshPrintLayout();
  }

  private refreshPrintLayout(): void {
    if (typeof window !== 'undefined') {
      setTimeout(() => window.dispatchEvent(new Event('resize')));
    }
  }

  back() {
    this.router.navigate(['/flock']);
  }

  print() {
    const pageTitle = document.title;
    const printTitle = `${this.locale.translate('title')} ${this.title} - ${formatDate(
      Date(),
      'dd-MM-yyyy',
      this.locale.getLocale(),
    )}`;
    if (window) {
      document.title = printTitle;
      window.print();
      document.title = pageTitle;
    }
  }

  isPrintableWeek(weeknr: number): boolean {
    switch (this.mode) {
      case 'all':
        return true;
      case 'summary':
        return weeknr === 30 || weeknr === 50;
    }
  }

  isValidNumber(value?: number) {
    return (
      !Number.isNaN(value) &&
      Number.isFinite(value) &&
      value.toString() !== 'NaN' &&
      value.toString() !== 'Infinity'
    );
  }

  getEggMarketingStandard(standard: number): string {
    if (standard) {
      const found = this.eggMarketingStandards.find((ems) => ems.id === standard).name;
      return this.locale.translate(`egg-marketing-standards.${found}`);
    }
    return '';
  }

  getUIValue(val: WeekRegistrationKPIItem): string {
    if (val?.value !== undefined && val?.value !== null && this.isValidNumber(val?.value)) {
      return this.decimalPipe.transform(
        val.value,
        val.decimalsPrecision > 0
          ? `1.${val.decimalsPrecision}-${val.decimalsPrecision}`
          : undefined,
      );
    }
    return '';
  }
}
